export type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined
>

type AnalyticsIdentity = {
  email: string
  name: string
}

type AnalyticsClient = {
  captureException?: (error: Error) => void
  capture?: (event: string, properties?: AnalyticsProperties) => void
  identify?: (userId: string, properties: AnalyticsIdentity) => void
  reset?: () => void
}

const MAX_PENDING_CLIENT_ACTIONS = 50
let analyticsClient: AnalyticsClient | undefined
const pendingClientActions: Array<(client: AnalyticsClient) => void> = []

/** Register the browser analytics client initialized by Next instrumentation. */
export function registerAnalyticsClient(client: AnalyticsClient | undefined): void {
  analyticsClient = client
  if (!client) return

  for (const action of pendingClientActions.splice(0)) {
    try {
      action(client)
    } catch {
      // Analytics must never break the UI.
    }
  }
}

function dispatchAnalyticsClient(action: (client: AnalyticsClient) => void): void {
  if (analyticsClient) {
    action(analyticsClient)
  } else if (pendingClientActions.length < MAX_PENDING_CLIENT_ACTIONS) {
    pendingClientActions.push(action)
  }
}
/**
 * Frontend analytics seam. No analytics provider is wired up yet, so this
 * records the event (visible in dev) and forwards it to a global analytics
 * object if one is ever connected (e.g. PostHog, or a GA `dataLayer`). That
 * keeps event call sites stable: connecting a real provider later is a drop-in
 * change here, with nothing to update at the call sites.
 */
export function trackEvent(
  name: string,
  properties?: AnalyticsProperties,
): void {
  if (typeof window === "undefined") return

  const w = window as typeof window & {
    dataLayer?: Array<Record<string, unknown>>
  }

  try {
    dispatchAnalyticsClient((client) => client.capture?.(name, properties))
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event: name, ...properties })
    }
  } catch {
    // Analytics must never break the UI.
  }

  if (process.env.NODE_ENV !== "production") {
    console.debug("[analytics]", name, properties ?? {})
  }
}

export function identifyAnalyticsUser(id: string, identity: AnalyticsIdentity): void {
  if (typeof window === "undefined") return

  try {
    dispatchAnalyticsClient((client) => client.identify?.(id, identity))
  } catch {
    // Analytics must never break the UI.
  }
}

export function resetAnalytics(): void {
  if (typeof window === "undefined") return

  try {
    dispatchAnalyticsClient((client) => client.reset?.())
  } catch {
    // Analytics must never break the UI.
  }
}

export function captureAnalyticsException(error: Error): void {
  if (typeof window === "undefined") return

  try {
    dispatchAnalyticsClient((client) => client.captureException?.(error))
  } catch {
    // Analytics must never break the UI.
  }
}
